import React from 'react';
import { LegalShell, Section, List } from '../../components/legal/LegalShell';

const PrivacyPage = () => (
  <LegalShell eyebrow="Legal" title="Privacy Policy" updated="07 August 2026">
    <p>Domestic Home Services Limited ("we", "us", "our") is committed to protecting your personal information and respecting your privacy.</p>
    <p>This Privacy Policy explains what personal information we collect, how we use it, who we share it with, how long we keep it and the rights available to you under applicable UK data protection legislation, including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>

    <Section n="1" title="Who We Are">
      <p><b>Domestic Home Services Limited</b><br/>Company Number: 17368184</p>
      <p>Registered Office:<br/>128 City Road<br/>London<br/>EC1V 2NX<br/>United Kingdom</p>
      <p>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>For any questions regarding this Privacy Policy or your personal information, please contact our Privacy Team using the contact details above.</p>
    </Section>

    <Section n="2" title="Information We Collect">
      <p>Depending on the services you use, we may collect:</p>
      <p><b>Personal Information</b></p>
      <List items={['Name', 'Postal address', 'Email address', 'Telephone number']} />
      <p><b>Maintenance Plan Information</b></p>
      <List items={['Plan number', 'Covered appliances or systems', 'Product brands and models', 'Service history', 'Repair requests', 'Engineer visit information']} />
      <p><b>Payment Information</b></p>
      <p>Where applicable, we may collect payment information required to administer your maintenance service plan.</p>
      <p>For payments made by bank transfer, we may retain transaction references and payment confirmations for accounting and verification purposes.</p>
      <p>We do not store customers' online banking credentials or payment passwords.</p>
      <p><b>Communications</b></p>
      <p>We may retain records of:</p>
      <List items={['Telephone calls', 'Emails', 'Letters', 'Live chat conversations', 'Customer support enquiries']} />
      <p>Telephone calls may be recorded for training, quality assurance, security and dispute resolution purposes.</p>
      <p><b>Website Information</b></p>
      <p>When you visit our website, we may automatically collect:</p>
      <List items={['IP address', 'Browser type', 'Device information', 'Pages visited', 'Time spent on our website', 'Referring website', 'Cookie preferences']} />
      <p><b>Additional Support Information</b></p>
      <p>Where you voluntarily tell us about accessibility requirements or circumstances requiring additional support, we may record this information only where reasonably necessary to assist you.</p>
    </Section>

    <Section n="3" title="How We Use Your Information">
      <p>We use personal information to:</p>
      <List items={[
        'Set up and manage maintenance service plans.',
        'Arrange engineer visits.',
        'Respond to enquiries.',
        'Provide customer support.',
        'Process payments.',
        'Prevent fraud.',
        'Improve our services.',
        'Meet legal, regulatory and accounting obligations.',
        'Maintain internal records.',
        'Keep our website secure.',
      ]} />
      <p>Where required by law, we will obtain your consent before sending marketing communications.</p>
      <p>You may withdraw your marketing consent at any time.</p>
    </Section>

    <Section n="4" title="Legal Basis for Processing">
      <p>Depending upon the circumstances, we process personal information because:</p>
      <List items={[
        'It is necessary to perform our contract with you.',
        'We have a legal obligation.',
        'We have a legitimate business interest that is not overridden by your rights.',
        'You have provided your consent.',
      ]} />
    </Section>

    <Section n="5" title="Who We Share Information With">
      <p>We may share information with:</p>
      <List items={[
        'Independent repair contractors and engineers.',
        'Payment providers and banking partners.',
        'IT service providers.',
        'Website hosting providers.',
        'Cloud storage providers.',
        'Professional advisers.',
        'Accountants.',
        'Government departments or law enforcement agencies where legally required.',
      ]} />
      <p>We only share information that is reasonably necessary for the relevant purpose.</p>
      <p>We never sell your personal information to third parties.</p>
    </Section>

    <Section n="6" title="International Transfers">
      <p>Where personal information is processed outside the United Kingdom, we take appropriate steps to ensure suitable safeguards are in place to protect your information in accordance with applicable data protection laws.</p>
    </Section>

    <Section n="7" title="How Long We Keep Information">
      <p>We keep personal information only for as long as reasonably necessary.</p>
      <p>Retention periods may vary depending on:</p>
      <List items={[
        'Legal obligations.',
        'Tax requirements.',
        'Accounting requirements.',
        'Ongoing customer relationships.',
        'Complaint handling.',
        'Fraud prevention.',
      ]} />
      <p>Where information is no longer required, it will be securely deleted or anonymised.</p>
    </Section>

    <Section n="8" title="Your Rights">
      <p>Subject to applicable law, you may have the right to:</p>
      <List items={[
        'Request access to your personal information.',
        'Request correction of inaccurate information.',
        'Request deletion of personal information.',
        'Request restriction of processing.',
        'Object to certain processing activities.',
        'Request transfer of your information where applicable.',
        'Withdraw consent where processing relies on consent.',
      ]} />
      <p>To exercise your rights, please contact us at:<br/>hello@domestichomeservices.com</p>
      <p>We will respond within the timeframe required by applicable law.</p>
    </Section>

    <Section n="9" title="Cookies">
      <p>Our website uses cookies and similar technologies to improve functionality, enhance security and understand how visitors use our website.</p>
      <p>Where required, we will ask for your consent before using non-essential cookies.</p>
      <p>Further information is available in our Cookie Policy.</p>
    </Section>

    <Section n="10" title="Data Security">
      <p>We take appropriate technical and organisational measures to protect personal information against:</p>
      <List items={['Unauthorised access.', 'Loss.', 'Misuse.', 'Alteration.', 'Disclosure.', 'Destruction.']} />
      <p>While no online system can guarantee complete security, we continually review and improve our security measures.</p>
    </Section>

    <Section n="11" title="Complaints">
      <p>If you have concerns regarding how we handle your personal information, please contact us first so we can investigate.</p>
      <p>If you remain dissatisfied, you have the right to contact the Information Commissioner's Office (ICO).</p>
    </Section>

    <Section n="12" title="Changes to This Privacy Policy">
      <p>We may update this Privacy Policy from time to time.</p>
      <p>The latest version will always be available on our website and will include the date it was last updated.</p>
    </Section>

    <Section n="13" title="Contact Us">
      <p><b>Domestic Home Services Limited</b><br/>Registered Office:<br/>128 City Road<br/>London<br/>EC1V 2NX<br/>United Kingdom</p>
      <p>Company Number: 17368184<br/>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
    </Section>
  </LegalShell>
);

export default PrivacyPage;
